/* eslint-disable @next/next/no-img-element */
import { Grid, Container } from '@achieve/ascend'
import { Typography } from 'components/Typography'
import { ScrollToTopButton } from 'components/Footer/UniversalFooter/ScrollToTopButton/ScrollToTopButton'
import SocialIcons from 'components/Footer/UniversalFooter/SocialIcons/SocialIcons'
import FooterCopyright from 'components/Footer/FooterCopyright'
import { getSlug } from 'utils/conversions'
import { useState, useEffect } from 'react'
import classNames from 'classnames'
import { AchieveLink } from 'components/AchieveLink'
import { getPageContentTypes } from 'utils/environmentConfig'
import { getSubdomain } from '@/utils/shared'
import { TypographyLongForm } from '@/components/Contentful'
import { useClientDashboardLoginOverlayTrigger } from 'hooks/useClientDashboardLoginOverlayTrigger/useClientDashboardLoginOverlayTrigger.js'
import { useAnalyticsTrackEvent } from 'hooks/useAnalyticsTrackEvent'
import miniGradientSvg from '../../../../../public/mini-gradient.svg'
import achivePro from '../../../../../public/achivePro.svg'
import smarterDebtSvg from '../../../../../public/smarter-debt.svg'
import equalHouseLogoSvg from '../../../../../public/equalHouseLogo.svg'

const Footer = ({
  footerNavigation,
  pageDisclosure,
  logoImage,
  siteEnv = 'achieve',
  logoImageDefault,
  logoHelpSubtext,
  brandCopy,
  trackingCB,
  copyrightMessage,
  showNav = true,
  showFooterSocialIcons = true,
  logoLink = true,
  showMarkers = true,
  markersSection = [],
  mainName,
  schema = {},
  className,
  config = {},
}) => {
  const resolvedSiteEnv = siteEnv ?? 'achieve'
  const isAchieve = resolvedSiteEnv === 'achieve'
  /** Opt-in via NEXT_PUBLIC_ACHIEVE_FOOTER_LIGHT: use blue-50 / blue-800 chrome instead of black Achieve footer */
  const useAchieveLightFooterChrome =
    isAchieve &&
    (process.env.NEXT_PUBLIC_ACHIEVE_FOOTER_LIGHT === 'true' ||
      process.env.NEXT_PUBLIC_ACHIEVE_FOOTER_LIGHT === '1')
  const useAchieveDarkFooterChrome = isAchieve && !useAchieveLightFooterChrome
  const isFdr = resolvedSiteEnv === 'website-fdr'
  const isWfs = resolvedSiteEnv === 'website-wfs'
  const isBills = resolvedSiteEnv === 'website-bills'
  const logoWrapperClasses = classNames(
    'relative block',
    isAchieve && 'w-[125px] h-[100px] m-auto lg:m-0 ',
    isFdr && 'w-[110px] h-[60px]',
    isBills && 'w-[110px] h-[60px]'
  )
  const { websiteField } = getPageContentTypes()
  const footerMainName = mainName ?? websiteField
  const [backLink, setBackLink] = useState('/')
  const { openOverlayInsteadOfNavigating } = useClientDashboardLoginOverlayTrigger()
  const { analyticsTrackEvent } = useAnalyticsTrackEvent()

  const isFdrLoginUrl = (url) => {
    if (!isFdr || !url) return false
    try {
      return new URL(url, 'http://_').pathname === '/login'
    } catch {
      return false
    }
  }

  const handleFdrLoginClick = (e, url, label) => {
    openOverlayInsteadOfNavigating(e)
    analyticsTrackEvent({
      nav_link_section: 'Footer',
      click_type: 'Link Click',
      click_id: label,
      click_url: url,
      click_text: `FDR-Web | ${label}`,
      track_event: 'global_footer',
      event_type: 'track',
    })
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol
      const host = window.location.host
      if (host) {
        if (getSubdomain(host, 'legal')) {
          const url = `${protocol}//${host}`.replaceAll('legal.', '').replaceAll('legal-', '')
          setBackLink(url)
        } else {
          setBackLink('/')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <footer
      className={classNames(
        useAchieveDarkFooterChrome
          ? 'bg-black text-[#F8F9FC] [&_a]:text-[#F8F9FC] pb-12 pt-12! lg:pb-16 lg:pt-16!'
          : isFdr
          ? 'bg-[#F9F9F9] text-blue-800 [&_a]:text-blue-800'
          : 'bg-blue-50 text-blue-800 [&_a]:text-blue-800',
        !showNav ? 'pt-0' : 'pt-8 lg:pt-16',
        className
      )}
      data-show-nav={showNav ? 'true' : 'false'}
      data-testid="footer"
      data-site={resolvedSiteEnv}
    >
      <Container
        maxWidth={false}
        disableGutters
        className={`max-w-[1440px] 
          ${isAchieve ? 'px-5 md:px-8' : 'px-16 md:px-8'}
          ${isWfs ? 'max-w-[700px]' : 'data-[narrow=true]:max-w-[1280px]'} `}
        data-narrow="true"
      >
        {showNav && (!isAchieve || useAchieveLightFooterChrome) && (
          <div
            className={classNames(
              'flex flex-col pb-8 border-t-0 border-solid border-x-0 border-b border-neutral-150 lg:flex-row lg:justify-between lg:mt-4',
              {
                'lg:flex-row-reverse': (footerNavigation?.length ?? 0) === 0,
              }
            )}
          >
            {footerNavigation.map(({ menuLabel, menuItems = [] }, menuIndex) => {
              return (
                <nav
                  className={classNames(
                    'flex-1 mt-8 lg:mt-0 lg:ml-4 [&_h4]:text-neutral-300',
                    menuIndex === 0 && 'max-w-none lg:max-w-[30%]'
                  )}
                  key={menuIndex}
                  data-testid="footer-menus"
                >
                  <Typography
                    component="h3"
                    fontWeight="bold"
                    variant="labelSm"
                    data-testid="footer-menu-title"
                  >
                    {menuLabel}
                  </Typography>
                  <ul className="p-0 m-0 list-none">
                    {menuItems.map(({ label, testid, url, target = '_self' }, menuItemIndex) => (
                      <li key={menuItemIndex} data-testid={testid} className="mt-2">
                        {isFdrLoginUrl(url) ? (
                          <a
                            data-testid={`footer-menu-${menuItemIndex}`}
                            href={url}
                            target={target}
                            className="text-neutral-975 no-underline hover:underline focus:underline cursor-pointer"
                            onClick={(e) => handleFdrLoginClick(e, url, label)}
                          >
                            <Typography
                              variant="labelSm"
                              fontWeight="regular"
                              className="cursor-pointer"
                            >
                              {label}
                            </Typography>
                          </a>
                        ) : (
                          <AchieveLink
                            data-testid={`footer-menu-${menuItemIndex}`}
                            href={url}
                            target={target}
                            withNextLink
                            track={{
                              nav_link_section: 'Footer Click',
                              click_type: 'Link Click',
                              click_id: label,
                              track_event: 'global_footer',
                              event_type: 'link_click',
                            }}
                            className="text-neutral-975  no-underline hover:underline focus:underline"
                          >
                            <Typography
                              variant="labelSm"
                              fontWeight="regular"
                              className="cursor-pointer"
                            >
                              {label}
                            </Typography>
                          </AchieveLink>
                        )}
                      </li>
                    ))}
                  </ul>
                </nav>
              )
            })}
            <div
              className="flex-2 pt-8 mt-8 lg:mt-0 border-t border-solid border-x-0 border-b-0 border-neutral-150 lg:pt-0 lg:border-t-0 lg:max-w-[336px] lg:ml-4"
              data-testid="footer-menus"
            >
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className="mb-4 flex items-center justify-start"
              >
                <Grid item>
                  {logoLink ? (
                    // eslint-disable-next-line @next/next/no-html-link-for-pages
                    <AchieveLink
                      className="relative block w-[71px] lg:w-[66px] m-auto lg:m-0"
                      href={backLink}
                      withNextLink
                      track={{
                        nav_link_section: 'Footer',
                        click_type: 'Link Click',
                        click_id: logoImage,
                        track_event: 'global_footer',
                        event_type: 'link_click',
                      }}
                      data-testid={`${getSlug(footerMainName)}-logo`}
                      aria-label={footerMainName}
                    >
                      <img
                        src={logoImage ? logoImage : logoImageDefault}
                        alt={`${footerMainName} Logomark`}
                        loading="lazy"
                      />
                    </AchieveLink>
                  ) : (
                    <div className="relative block w-[71px] lg:w-[66px]">
                      <img
                        src={logoImageDefault}
                        alt={`${footerMainName} Logomark`}
                        loading="lazy"
                      />
                    </div>
                  )}
                </Grid>
                <Grid item>
                  <ScrollToTopButton
                    data-testid="scroll-to-top-button"
                    className="block lg:hidden"
                  />
                </Grid>
              </Grid>
              {brandCopy && (
                <Typography
                  component="p"
                  variant="bodyXs"
                  fontWeight="regular"
                  className="text-left lg:text-right text-blue-800"
                >
                  {brandCopy}
                </Typography>
              )}
              {logoHelpSubtext && (
                <TypographyLongForm
                  content={logoHelpSubtext}
                  className="bodyXs! text-left lg:text-right text-blue-800 [&_a]:underline"
                />
              )}
              {showFooterSocialIcons && (
                <Grid item className="mt-4 md:mb-0">
                  <SocialIcons
                    items={schema?.oganizationSocialMediaLinks}
                    trackingCB={trackingCB}
                  />
                </Grid>
              )}
              {markersSection?.length > 0 && showMarkers && (
                <Grid container gap={3} alignItems="center" className="mt-4 lg:justify-end">
                  {markersSection.map((item, index) => (
                    <Grid
                      item
                      key={`marker-footer-${index}`}
                      position="relative"
                      className="max-w-[80px] overflow-hidden"
                    >
                      {item}
                    </Grid>
                  ))}
                </Grid>
              )}
            </div>
          </div>
        )}

        {showNav && useAchieveDarkFooterChrome && (
          <div
            className={classNames(
              config?.showAchievePro
                ? 'flex flex-col justify-start lg:flex-row lg:justify-left lg:mt-4'
                : 'flex flex-col lg:flex-row lg:justify-left lg:mt-4'
            )}
          >
            <div
              className="flex-2 lg:mt-0 lg:border-t-0 lg:max-w-[336px] w-full lg:w-auto"
              data-testid="footer-menus"
            >
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className={
                  config?.showAchievePro
                    ? 'flex-row lg:block justify-left lg:justify-left'
                    : 'mb-4 flex-col lg:block justify-left lg:justify-left'
                }
              >
                <Grid item>
                  {logoLink ? (
                    <>
                      <AchieveLink
                        className={!config?.showAchievePro && logoWrapperClasses}
                        href={backLink}
                        withNextLink
                        track={{
                          nav_link_section: 'Footer',
                          click_type: 'Link Click',
                          click_id: logoImage,
                          track_event: 'global_footer',
                          event_type: 'link_click',
                        }}
                        data-testid={`${getSlug(footerMainName)}-logo`}
                        aria-label={footerMainName}
                      >
                        <img
                          src={config?.showAchievePro ? achivePro.src : miniGradientSvg.src}
                          alt={`${footerMainName} Logomark`}
                          loading="lazy"
                        />
                      </AchieveLink>
                      {!(config?.showAchievePro === true) && (
                        <img
                          src={smarterDebtSvg.src}
                          className="my-5"
                          alt={`${footerMainName} Logomark`}
                          height={88}
                          width={240}
                          loading="lazy"
                        />
                      )}
                    </>
                  ) : (
                    <div className={logoWrapperClasses}>
                      <img
                        src={miniGradientSvg.src}
                        alt={`${footerMainName} Logomark`}
                        loading="lazy"
                      />
                      <img
                        src={smarterDebtSvg.src}
                        className="my-5"
                        alt={`${footerMainName} Logomark`}
                        height={88}
                        width={240}
                        loading="lazy"
                      />
                    </div>
                  )}
                </Grid>
              </Grid>
              {showFooterSocialIcons && (
                <Grid item className="mt-4 md:mb-0">
                  <SocialIcons
                    defaultIconColor="white"
                    items={schema?.oganizationSocialMediaLinks}
                    trackingCB={trackingCB}
                  />
                </Grid>
              )}
            </div>
            {footerNavigation?.length > 0 && (
              <div
                className="columns-2 lg:grid lg:grid-cols-3 lg:gap-y-8 w-full pt-8 lg:pt-0 mt-8 lg:mt-0 border-b-0 border-solid border-x-0 border-t border-[#F8F9FC] lg:border-t-0 lg:pl-40 xl:pl-44 lg:gap-x-20 xl:gap-x-44"
                data-testid="footer-menus"
                style={{ columnGap: '1rem' }}
              >
                {footerNavigation.map(({ menuLabel, menuItems = [] }, menuIndex) => {
                  return (
                    <nav
                      className="[&_h4]:text-[19B1E2] break-inside-avoid py-1 mb-3 lg:mb-0 first:mt-0"
                      key={menuIndex}
                      data-testid="footer-menus"
                    >
                      <Typography
                        component="h3"
                        fontWeight="bold"
                        variant="labelLg"
                        className="font-bold! text-[#19B1E2] pb-[23px] m-0"
                        data-testid="footer-menu-title"
                      >
                        {menuLabel}
                      </Typography>
                      <ul className="p-0 m-0 list-none">
                        {menuItems.map(
                          ({ label, testid, url, target = '_self' }, menuItemIndex) => (
                            <li key={menuItemIndex} data-testid={testid} className="mb-[10px]">
                              <AchieveLink
                                data-testid={`footer-menu-${menuItemIndex}`}
                                href={url}
                                target={target}
                                withNextLink
                                track={{
                                  nav_link_section: 'Footer Click',
                                  click_type: 'Link Click',
                                  click_id: label,
                                  track_event: 'global_footer',
                                  event_type: 'link_click',
                                }}
                                className="text-white  no-underline hover:underline focus:underline"
                              >
                                <Typography
                                  variant="labelSm"
                                  fontWeight="regular"
                                  className="cursor-pointer"
                                >
                                  {label}
                                </Typography>
                              </AchieveLink>
                            </li>
                          )
                        )}
                      </ul>
                    </nav>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {pageDisclosure?.map((dataDisclosure, index) => {
          if (!dataDisclosure) return
          return (
            <div
              key={'dislosure-' + index}
              className={`${
                useAchieveDarkFooterChrome
                  ? 'text-[#F8F9FC] mt-12 border-t border-solid border-x-0 border-b-0 border-neutral-150  pt-12'
                  : 'text-blue-800 mt-12 '
              } [&_p]:leading-normal [&_p:not(:last-child)]:mb-4 [&_ol]:pl-4 [&_ol>li]:mb-6 [&_li::marker]:text-[11px]`}
              data-testid="footer-page-disclosure"
            >
              {dataDisclosure}
            </div>
          )
        })}
        <div
          className={`flex flex-col-reverse lg:flex-row justify-between ${
            useAchieveDarkFooterChrome ? 'py-4' : 'mt-8 py-8'
          }`}
        >
          <Grid
            container
            justifyContent="space-between"
            className={`flex-col-reverse  ${
              useAchieveDarkFooterChrome ? 'items-start' : 'items-end'
            } lg:flex-row lg:items-center`}
          >
            <Grid item>
              <FooterCopyright
                content={copyrightMessage}
                className={useAchieveDarkFooterChrome ? 'text-[#F8F9FC]' : 'text-blue-800'}
                data-testid="footer-copyright"
              />
              {isAchieve && (
                <img
                  src={equalHouseLogoSvg.src}
                  width={62}
                  alt="Equal Housing logo"
                  loading="lazy"
                  className="pt-2 block"
                />
              )}
            </Grid>
          </Grid>
        </div>
      </Container>
    </footer>
  )
}

export { Footer }
